//
// src/App.js
//
// Main application shell and route configuration.
//
// Responsibilities:
// - Restore authenticated user state
// - Keep account status synchronized
// - Keep subscription/tier status synchronized
// - Provide tier + role based navigation
// - Route authenticated users to permitted pages
// - Keep authentication and licensing as separate concerns
//
// Licensing policy:
// - Expired licences NEVER log a customer out.
// - Expired customers retain access to their workspace.
// - Billing remains available for subscription renewal.
// - New-sale creation is blocked by the backend with HTTP 402.
//

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";

import {
  AppBar,
  Box,
  Button,
  Chip,
  Tab,
  Tabs,
  Toolbar,
} from "@mui/material";

import API from "./api";

import Login from "./pages/Login";
import RegisterTenant from "./components/RegisterTenant";
import PrinterTest from "./pages/PrinterTest";
import Users from "./pages/Users";
import Products from "./pages/Products";
import Sales from "./pages/Sales";
import Cashier from "./pages/Cashier";
import StockIn from "./pages/StockIn";
import Reports from "./pages/Reports";
import AuditLogs from "./pages/AuditLogs";
import AdminBilling from "./pages/AdminBilling";

import ProtectedRoute from "./components/ProtectedRoute";


// =====================================================
// Tier Configuration
// =====================================================

const TIER_LEVELS = {
  silver: 1,
  gold: 2,
  platinum: 3,
};


// =====================================================
// Navigation / Permission Matrix
// =====================================================

const TAB_CONFIG = [
  {
    id: "Cashier",
    label: "Cashier",
    route: "/cashier",
    minTier: "silver",
    roles: [
      "a",
      "admin",
      "s",
      "supervisor",
      "c",
      "cashier",
    ],
  },

  {
    id: "StockIn",
    label: "Stock In",
    route: "/stock-in",
    minTier: "silver",
    roles: [
      "a",
      "admin",
      "s",
      "supervisor",
    ],
  },

  {
    id: "Products",
    label: "Products",
    route: "/products",
    minTier: "silver",
    roles: [
      "a",
      "admin",
      "s",
      "supervisor",
    ],
  },

  {
    id: "Sales",
    label: "Sales",
    route: "/sales",
    minTier: "silver",
    roles: [
      "a",
      "admin",
      "s",
      "supervisor",
      "c",
      "cashier",
    ],
  },

  {
    id: "Users",
    label: "Users",
    route: "/users",
    minTier: "gold",
    roles: [
      "a",
      "admin",
    ],
  },

  {
    id: "Reports",
    label: "Reports",
    route: "/reports",
    minTier: "gold",
    roles: [
      "a",
      "admin",
      "s",
      "supervisor",
    ],
  },

  {
    id: "Audit",
    label: "Audit",
    route: "/audit-logs",
    minTier: "platinum",
    roles: [
      "a",
      "admin",
    ],
  },

  {
    id: "Billing",
    label: "Billing",
    route: "/billing",
    minTier: "silver",
    roles: [
      "a",
      "admin",
      "s",
      "supervisor",
    ],
  },

  {
    id: "Printer",
    label: "Printer Test",
    route: "/printer-test",
    minTier: "silver",
    roles: [
      "a",
      "admin",
    ],
  },
];


// =====================================================
// Helpers
// =====================================================

function getStoredUser() {
  const rawUser =
    localStorage.getItem("user");

  if (!rawUser) {
    return null;
  }

  try {
    return JSON.parse(rawUser);
  } catch (error) {
    console.warn(
      "Stored user information could not be read:",
      error
    );

    localStorage.removeItem("user");

    return null;
  }
}


function normalizeRole(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}


function normalizeTier(value) {
  const tier =
    String(value || "")
      .trim()
      .toLowerCase();

  // Compatibility with legacy trial records.
  if (
    tier === "standard" ||
    tier === "basic"
  ) {
    return "silver";
  }

  if (
    tier === "silver" ||
    tier === "gold" ||
    tier === "platinum"
  ) {
    return tier;
  }

  return "silver";
}


function clearAuthenticationStorage() {
  localStorage.removeItem("user");
  localStorage.removeItem("token");
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("tenant_id");
  localStorage.removeItem("tenant_tier");

  sessionStorage.removeItem("user");
  sessionStorage.removeItem("token");
  sessionStorage.removeItem("access_token");
  sessionStorage.removeItem("refresh_token");
  sessionStorage.removeItem("tenant_id");
}


// =====================================================
// Application
// =====================================================

export default function App() {
  const navigate =
    useNavigate();

  const location =
    useLocation();


  // ===================================================
  // Authenticated User
  // ===================================================

  const [
    loggedUser,
    setLoggedUser,
  ] = useState(
    () => getStoredUser()
  );


  const loggedUserId =
    loggedUser?.id ?? null;


  // ===================================================
  // Tier State
  // ===================================================

  const [
    tenantTier,
    setTenantTier,
  ] = useState(() => {
    const storedUser =
      getStoredUser();

    return normalizeTier(
      localStorage.getItem(
        "tenant_tier"
      ) ||
      storedUser?.tenant_tier ||
      storedUser?.tier ||
      "silver"
    );
  });


  // ===================================================
  // Licence / Commercial Entitlement State
  // ===================================================

  const [
    licenseState,
    setLicenseState,
  ] = useState({
    status: "unknown",
    isActive: true,
    renewalRequired: false,
    canCreateSales: true,
  });


  // ===================================================
  // Navigation State
  // ===================================================

  const [
    tab,
    setTab,
  ] = useState(0);


  // ===================================================
  // Logout
  // ===================================================

  const handleLogout =
    useCallback(() => {
      clearAuthenticationStorage();

      setLoggedUser(null);

      setTenantTier(
        "silver"
      );

      setLicenseState({
        status: "unknown",
        isActive: false,
        renewalRequired: false,
        canCreateSales: false,
      });

      navigate(
        "/login",
        {
          replace: true,
        }
      );
    }, [
      navigate,
    ]);


  // ===================================================
  // Listen for Billing / Tier Updates
  // ===================================================

  useEffect(() => {
    const handleTierUpdated =
      (event) => {
        const incomingTier =
          event?.detail?.tier;

        if (!incomingTier) {
          return;
        }

        const cleanTier =
          normalizeTier(
            incomingTier
          );

        setTenantTier(
          cleanTier
        );

        localStorage.setItem(
          "tenant_tier",
          cleanTier
        );
      };


    const handleLicenseUpdated =
      (event) => {
        const data =
          event?.detail;

        if (!data) {
          return;
        }

        const cleanTier =
          normalizeTier(
            data.tier ||
            data.plan_type ||
            localStorage.getItem(
              "tenant_tier"
            )
        );

        const isActive =
          Boolean(
            data.is_active
          );

        const renewalRequired =
          Boolean(
            data.renewal_required
          );

        const canCreateSales =
          data.can_create_sales !==
          false;

        const statusValue =
          String(
            data.status ||
            (
              isActive
                ? "active"
                : "inactive"
            )
          )
            .trim()
            .toLowerCase();

        setTenantTier(
          cleanTier
        );

        setLicenseState({
          status:
            statusValue,

          isActive,

          renewalRequired,

          canCreateSales,
        });

        localStorage.setItem(
          "tenant_tier",
          cleanTier
        );
      };


    window.addEventListener(
      "tierUpdated",
      handleTierUpdated
    );

    window.addEventListener(
      "licenseUpdated",
      handleLicenseUpdated
    );


    return () => {
      window.removeEventListener(
        "tierUpdated",
        handleTierUpdated
      );

      window.removeEventListener(
        "licenseUpdated",
        handleLicenseUpdated
      );
    };
  }, []);


  // ===================================================
  // Account + Subscription Synchronization
  // ===================================================

  useEffect(() => {
    if (!loggedUserId) {
      return undefined;
    }


    if (
      [
        "/login",
        "/register-tenant",
      ].includes(
        location.pathname
      )
    ) {
      return undefined;
    }


    let cancelled =
      false;


    // -------------------------------------------------
    // Account Status
    // -------------------------------------------------

    const syncAccountStatus =
      async () => {
        const token =
          localStorage.getItem(
            "token"
          );


        if (!token) {
          if (!cancelled) {
            handleLogout();
          }

          return false;
        }


        try {
          const response =
            await API.get(
              `/users/${loggedUserId}`
            );


          if (cancelled) {
            return false;
          }


          const userData =
            response.data;


          if (
            userData?.is_active ===
            false
          ) {
            window.alert(
              "Your account is currently inactive. " +
              "Please contact your workspace administrator " +
              "for assistance."
            );

            handleLogout();

            return false;
          }


          if (userData) {
            setLoggedUser(
              (previousUser) => {
                if (!previousUser) {
                  return previousUser;
                }

                const refreshedUser = {
                  ...previousUser,
                  ...userData,
                };

                localStorage.setItem(
                  "user",
                  JSON.stringify(
                    refreshedUser
                  )
                );

                return refreshedUser;
              }
            );
          }


          return true;

        } catch (error) {
          if (cancelled) {
            return false;
          }


          const statusCode =
            error?.response?.status;


          console.error(
            "Unable to refresh account status:",
            error
          );


          // Authentication failure.
          if (statusCode === 401) {
            handleLogout();

            return false;
          }


          // Authorization restrictions must not
          // automatically destroy the login session.
          if (statusCode === 403) {
            console.warn(
              "Account status refresh was not permitted. " +
              "The current authenticated session will remain active."
            );

            return true;
          }


          // Temporary server/network errors must not
          // eject the customer from the application.
          console.warn(
            "Account status could not be refreshed. " +
            "The current session will remain available."
          );


          return true;
        }
      };


    // -------------------------------------------------
    // Subscription Status
    // -------------------------------------------------

    const syncSubscriptionStatus =
      async () => {
        try {
          const response =
            await API.get(
              "/billing/status"
            );


          if (cancelled) {
            return;
          }


          const data =
            response.data || {};


          const cleanTier =
            normalizeTier(
              data.tier ||
              data.plan_type ||
              localStorage.getItem(
                "tenant_tier"
              ) ||
              "silver"
            );


          const isActive =
            Boolean(
              data.is_active
            );


          const renewalRequired =
            Boolean(
              data.renewal_required
            );


          const canCreateSales =
            data.can_create_sales !==
            false;


          const statusValue =
            String(
              data.status ||
              (
                isActive
                  ? "active"
                  : "inactive"
              )
            )
              .trim()
              .toLowerCase();


          setTenantTier(
            cleanTier
          );


          setLicenseState({
            status:
              statusValue,

            isActive,

            renewalRequired,

            canCreateSales,
          });


          localStorage.setItem(
            "tenant_tier",
            cleanTier
          );


          window.dispatchEvent(
            new CustomEvent(
              "tierUpdated",
              {
                detail: {
                  tier:
                    cleanTier,
                },
              }
            )
          );


          window.dispatchEvent(
            new CustomEvent(
              "licenseUpdated",
              {
                detail: {
                  ...data,
                  tier:
                    cleanTier,
                },
              }
            )
          );


          if (
            renewalRequired ||
            !canCreateSales
          ) {
            console.info(
              "Workspace subscription requires attention. " +
              "The customer remains signed in and may renew through Billing."
            );
          }

        } catch (error) {
          if (cancelled) {
            return;
          }


          // IMPORTANT:
          // Licensing failure is never an authentication failure.
          console.warn(
            "Workspace subscription status could not be refreshed. " +
            "The current session will remain available.",
            error
          );
        }
      };


    // -------------------------------------------------
    // Combined Synchronization
    // -------------------------------------------------

    const syncApplicationState =
      async () => {
        const accountAvailable =
          await syncAccountStatus();


        if (
          !accountAvailable ||
          cancelled
        ) {
          return;
        }


        await syncSubscriptionStatus();
      };


    syncApplicationState();


    return () => {
      cancelled = true;
    };

  }, [
    location.pathname,
    loggedUserId,
    handleLogout,
  ]);


  // ===================================================
  // Tier + Role Navigation
  // ===================================================

  const visibleTabs =
    useMemo(() => {
      if (!loggedUser) {
        return [];
      }


      const role =
        normalizeRole(
          loggedUser.role
        ) || "c";


      const tierWeight =
        TIER_LEVELS[
          tenantTier
        ] ||
        TIER_LEVELS.silver;


      return TAB_CONFIG.filter(
        (item) => {
          const hasRoleAccess =
            item.roles.includes(
              role
            );


          const requiredTierWeight =
            TIER_LEVELS[
              item.minTier
            ] ||
            TIER_LEVELS.silver;


          const hasTierAccess =
            tierWeight >=
            requiredTierWeight;


          return (
            hasRoleAccess &&
            hasTierAccess
          );
        }
      );

    }, [
      loggedUser,
      tenantTier,
    ]);


  // ===================================================
  // Synchronize Tab With Route
  // ===================================================

  useEffect(() => {
    if (
      !loggedUser ||
      visibleTabs.length === 0
    ) {
      return;
    }


    const currentIndex =
      visibleTabs.findIndex(
        (item) =>
          location.pathname.startsWith(
            item.route
          )
      );


    setTab(
      currentIndex >= 0
        ? currentIndex
        : 0
    );

  }, [
    location.pathname,
    loggedUser,
    visibleTabs,
  ]);


  // ===================================================
  // Navigation
  // ===================================================

  const handleTabChange =
    (
      _event,
      newValue
    ) => {
      const selectedTab =
        visibleTabs[
          newValue
        ];


      if (!selectedTab) {
        return;
      }


      setTab(
        newValue
      );


      navigate(
        selectedTab.route
      );
    };


  const isRouteAllowed =
    useCallback(
      (routePath) => {
        return visibleTabs.some(
          (item) =>
            item.route ===
            routePath
        );
      },
      [
        visibleTabs,
      ]
    );


  // ===================================================
  // Licence Badge
  // ===================================================

  const licenseBadge =
    useMemo(() => {
      if (
        licenseState.renewalRequired ||
        !licenseState.canCreateSales
      ) {
        return {
          label:
            `${tenantTier.toUpperCase()} · RENEWAL DUE`,

          color:
            "warning",
        };
      }


      if (
        tenantTier ===
        "platinum"
      ) {
        return {
          label:
            "PLATINUM TIER",

          color:
            "secondary",
        };
      }


      if (
        tenantTier ===
        "gold"
      ) {
        return {
          label:
            "GOLD TIER",

          color:
            "warning",
        };
      }


      return {
        label:
          "SILVER TIER",

        color:
          "default",
      };

    }, [
      licenseState.renewalRequired,
      licenseState.canCreateSales,
      tenantTier,
    ]);


  // ===================================================
  // Safe Tab Value
  // ===================================================

  const safeTabValue =
    visibleTabs.length > 0
      ? Math.min(
          tab,
          visibleTabs.length - 1
        )
      : false;


  // ===================================================
  // Render
  // ===================================================

  return (
    <div>
      {/* =================================================
          Application Header
      ================================================= */}

      {loggedUser && (
        <AppBar
          position="static"
        >
          <Toolbar>
            {visibleTabs.length > 0 && (
              <Tabs
                value={
                  safeTabValue
                }
                onChange={
                  handleTabChange
                }
                textColor="inherit"
                variant="scrollable"
                scrollButtons="auto"
              >
                {visibleTabs.map(
                  (item) => (
                    <Tab
                      key={
                        item.id
                      }
                      label={
                        item.label
                      }
                    />
                  )
                )}
              </Tabs>
            )}


            <Box
              sx={{
                flexGrow: 1,
              }}
            />


            <Chip
              label={
                licenseBadge.label
              }
              size="small"
              color={
                licenseBadge.color
              }
              sx={{
                mr: 2,
                fontWeight: 700,
              }}
            />


            <Button
              color="inherit"
              onClick={
                handleLogout
              }
            >
              Logout
            </Button>
          </Toolbar>
        </AppBar>
      )}


      {/* =================================================
          Routes
      ================================================= */}

      <Routes>
        {/* -----------------------------------------------
            Login
        ----------------------------------------------- */}

        <Route
          path="/login"
          element={
            !loggedUser
              ? (
                  <Login
                    setLoggedUser={
                      setLoggedUser
                    }
                  />
                )
              : (
                  <Navigate
                    to="/"
                    replace
                  />
                )
          }
        />


        {/* -----------------------------------------------
            Tenant Registration
        ----------------------------------------------- */}

        <Route
          path="/register-tenant"
          element={
            !loggedUser
              ? (
                  <RegisterTenant />
                )
              : (
                  <Navigate
                    to="/"
                    replace
                  />
                )
          }
        />


        {/* -----------------------------------------------
            Root
        ----------------------------------------------- */}

        <Route
          path="/"
          element={
            loggedUser
              ? (
                  visibleTabs.length > 0
                    ? (
                        <Navigate
                          to={
                            visibleTabs[0]
                              .route
                          }
                          replace
                        />
                      )
                    : (
                        <Navigate
                          to="/cashier"
                          replace
                        />
                      )
                )
              : (
                  <Navigate
                    to="/login"
                    replace
                  />
                )
          }
        />


        {/* -----------------------------------------------
            Cashier
        ----------------------------------------------- */}

        <Route
          path="/cashier"
          element={
            isRouteAllowed(
              "/cashier"
            )
              ? (
                  <ProtectedRoute
                    roles={[
                      "admin",
                      "supervisor",
                      "cashier",
                      "a",
                      "s",
                      "c",
                    ]}
                  >
                    <Cashier
                      loggedUser={
                        loggedUser
                      }
                    />
                  </ProtectedRoute>
                )
              : (
                  <Navigate
                    to="/"
                    replace
                  />
                )
          }
        />


        {/* -----------------------------------------------
            Stock In
        ----------------------------------------------- */}

        <Route
          path="/stock-in"
          element={
            isRouteAllowed(
              "/stock-in"
            )
              ? (
                  <ProtectedRoute
                    roles={[
                      "admin",
                      "supervisor",
                      "a",
                      "s",
                    ]}
                  >
                    <StockIn
                      loggedUser={
                        loggedUser
                      }
                    />
                  </ProtectedRoute>
                )
              : (
                  <Navigate
                    to="/"
                    replace
                  />
                )
          }
        />


        {/* -----------------------------------------------
            Users
        ----------------------------------------------- */}

        <Route
          path="/users"
          element={
            isRouteAllowed(
              "/users"
            )
              ? (
                  <ProtectedRoute
                    roles={[
                      "admin",
                      "a",
                    ]}
                  >
                    <Users />
                  </ProtectedRoute>
                )
              : (
                  <Navigate
                    to="/"
                    replace
                  />
                )
          }
        />


        {/* -----------------------------------------------
            Products
        ----------------------------------------------- */}

        <Route
          path="/products"
          element={
            isRouteAllowed(
              "/products"
            )
              ? (
                  <ProtectedRoute
                    roles={[
                      "admin",
                      "supervisor",
                      "a",
                      "s",
                    ]}
                  >
                    <Products />
                  </ProtectedRoute>
                )
              : (
                  <Navigate
                    to="/"
                    replace
                  />
                )
          }
        />


        {/* -----------------------------------------------
            Sales
        ----------------------------------------------- */}

        <Route
          path="/sales"
          element={
            isRouteAllowed(
              "/sales"
            )
              ? (
                  <ProtectedRoute
                    roles={[
                      "admin",
                      "supervisor",
                      "cashier",
                      "a",
                      "s",
                      "c",
                    ]}
                  >
                    <Sales />
                  </ProtectedRoute>
                )
              : (
                  <Navigate
                    to="/"
                    replace
                  />
                )
          }
        />


        {/* -----------------------------------------------
            Reports
        ----------------------------------------------- */}

        <Route
          path="/reports"
          element={
            isRouteAllowed(
              "/reports"
            )
              ? (
                  <ProtectedRoute
                    roles={[
                      "admin",
                      "supervisor",
                      "a",
                      "s",
                    ]}
                  >
                    <Reports />
                  </ProtectedRoute>
                )
              : (
                  <Navigate
                    to="/"
                    replace
                  />
                )
          }
        />


        {/* -----------------------------------------------
            Audit Logs
        ----------------------------------------------- */}

        <Route
          path="/audit-logs"
          element={
            isRouteAllowed(
              "/audit-logs"
            )
              ? (
                  <ProtectedRoute
                    roles={[
                      "admin",
                      "a",
                    ]}
                  >
                    <AuditLogs />
                  </ProtectedRoute>
                )
              : (
                  <Navigate
                    to="/"
                    replace
                  />
                )
          }
        />


        {/* -----------------------------------------------
            Billing
        ----------------------------------------------- */}

        <Route
          path="/billing"
          element={
            isRouteAllowed(
              "/billing"
            )
              ? (
                  <ProtectedRoute
                    roles={[
                      "admin",
                      "supervisor",
                      "a",
                      "s",
                    ]}
                  >
                    <AdminBilling
                      loggedUser={
                        loggedUser
                      }
                    />
                  </ProtectedRoute>
                )
              : (
                  <Navigate
                    to="/"
                    replace
                  />
                )
          }
        />


        {/* -----------------------------------------------
            Printer Test
        ----------------------------------------------- */}

        <Route
          path="/printer-test"
          element={
            isRouteAllowed(
              "/printer-test"
            )
              ? (
                  <ProtectedRoute
                    roles={[
                      "admin",
                      "a",
                    ]}
                  >
                    <PrinterTest />
                  </ProtectedRoute>
                )
              : (
                  <Navigate
                    to="/"
                    replace
                  />
                )
          }
        />


        {/* -----------------------------------------------
            Fallback
        ----------------------------------------------- */}

        <Route
          path="*"
          element={
            <Navigate
              to="/"
              replace
            />
          }
        />
      </Routes>
    </div>
  );
}